import React, { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { formatBirthday, Translate } from '@ana/shared';
import { DialogNames } from 'src/components/dialogs/DialogRoot';
import { EndBar } from 'src/components/EndBar/EndBar';
import { useAnaContext, useAnaDispatch, setAnswers, setLoading, useInit } from 'src/components/providers/AnaProvider';
import { useDialog } from 'src/components/providers/DialogProvider';
import StepperProgress from 'src/components/StepperProgress';
import { SubTitle } from 'src/components/Subtitle/SubTitle';
import usePerson from 'src/graphql/hooks/usePerson';
import { stripBase } from 'src/helpers/signature';
import { useAnamnesisById } from 'src/hooks/api/useAnamnesisById';
import { useAnamnesisFromHistory } from 'src/hooks/api/useAnamnesisFromHistory';
import { useAnamnesisMutation } from 'src/hooks/api/useAnamnesisMutation';
import { useOptins } from 'src/hooks/api/useOptins';
import { useOptinsMutation } from 'src/hooks/api/useOptinsMutation';
import { useAnaNavigation, AnaMode } from 'src/hooks/useAnaNavigation';
import InternalTemplate from 'src/templates/InternalTemplate';
import { PersonalInformationContent } from '../PersonalInformation/PersonalInformation';
import * as S from './Anamnesis.styles';
const EndBarDataAttr = 'ana-end-bar-data-attr';
export let AnaSection = /*#__PURE__*/function (AnaSection) {
  AnaSection["PersonalInformation"] = "personal-information";
  AnaSection["AllergiesAndMedications"] = "allergies-and-medications";
  AnaSection["ContactDetails"] = "contact-details";
  AnaSection["Insurance"] = "insurance";
  AnaSection["Preconditions1"] = "preconditions-1";
  AnaSection["Preconditions2"] = "preconditions-2";
  AnaSection["Preconditions3"] = "preconditions-3";
  AnaSection["Preparations"] = "preparations";
  AnaSection["Wishes1"] = "wishes-1";
  AnaSection["Wishes2"] = "wishes-2";
  AnaSection["Wishes3"] = "wishes-3";
  AnaSection["Consents"] = "consents";
  AnaSection["FactoringAgreement"] = "factoring-agreement";
  AnaSection["Success"] = "success";
  return AnaSection;
}({});
const titles = {
  [AnaSection.PersonalInformation]: 'category.general_information',
  [AnaSection.ContactDetails]: 'category.general_information',
  [AnaSection.Insurance]: 'category.general_information',
  [AnaSection.Preconditions1]: 'category.anamnesis',
  [AnaSection.Preconditions2]: 'category.anamnesis',
  [AnaSection.Preconditions3]: 'category.anamnesis',
  [AnaSection.AllergiesAndMedications]: 'subcategory.allergies_and_medications',
  [AnaSection.Preparations]: 'category.additional_details',
  [AnaSection.Wishes1]: 'category.additional_details',
  [AnaSection.Wishes2]: 'category.additional_details',
  [AnaSection.Wishes3]: 'category.additional_details',
  [AnaSection.Consents]: 'category.finalize',
  [AnaSection.FactoringAgreement]: 'screen.factoring_agreement.title',
  [AnaSection.Success]: 'screen.success.title'
};
const subTitles = optins => ({
  [AnaSection.PersonalInformation]: 'subcategory.personal_data',
  [AnaSection.ContactDetails]: 'subcategory.contact_details',
  [AnaSection.Insurance]: 'subcategory.insurance_details',
  [AnaSection.Preconditions1]: 'subcategory.pre_existing_conditions',
  [AnaSection.Preconditions2]: 'subcategory.pre_existing_conditions',
  [AnaSection.Preconditions3]: 'subcategory.pre_existing_conditions',
  [AnaSection.AllergiesAndMedications]: 'category.anamnesis',
  [AnaSection.Preparations]: 'subcategory.in_preparation',
  [AnaSection.Wishes1]: 'subcategory.let_us_know_about_you',
  [AnaSection.Wishes2]: 'subcategory.let_us_know_about_you',
  [AnaSection.Wishes3]: 'subcategory.let_us_know_about_you',
  [AnaSection.Consents]: (() => {
    if (!optins?.confidentialityAgreementAccepted || !optins?.marketingCommunicationConsentAccepted) {
      return 'subcategory.consents_and_cancelation_policy';
    }
    return 'subcategory.only_cancelation_policy';
  })(),
  [AnaSection.FactoringAgreement]: 'subcategory.convenient_payment',
  [AnaSection.Success]: 'subcategory.success'
});
const AnamnesisScreen = ({
  children = PersonalInformationContent,
  EndBarContent
}) => {
  const {
    answers,
    clinicReferenceId,
    factoringAgreementMissing,
    referenceId,
    loading
  } = useAnaContext();
  const {
    query: {
      mode
    }
  } = useRouter();
  const anaMode = AnaMode[String(mode).toUpperCase()];
  const {
    step,
    goToNextStep,
    indices,
    allDone
  } = useAnaNavigation({
    mode: anaMode
  });
  const {
    openDialog,
    closeDialog
  } = useDialog();
  const {
    mutate: mutateAna
  } = useAnamnesisMutation();
  const {
    mutate: mutateOptins
  } = useOptinsMutation({
    clinicReferenceId
  });
  const {
    data: optinsData
  } = useOptins({
    enabled: step === AnaSection.Consents
  });
  const optins = optinsData?.find(itm => itm.clinicReferenceId === clinicReferenceId)?.optIns;
  const search = useSearchParams();
  const id = search.get('referenceId');
  // When navigating from /documents we pass `historyReferenceId` instead of
  // the legacy `referenceId`, because the new API loads the prefill data by
  // anamnesis-history id (different ID space than the old /anamnesis/{id}).
  const historyId = search.get('historyReferenceId');
  // /documents also tells us whether the clinic actually requires a factoring
  // agreement (it comes from the per-step status in /clinics/documents). When
  // explicitly "false", skip the factoring step in the form flow.
  const factoringRequiredParam = search.get('factoringRequired');
  const factoringRequired = factoringRequiredParam === null ? undefined : factoringRequiredParam !== 'false';
  const dispatch = useAnaDispatch();
  const {
    initRenewOrEdit,
    initNew
  } = useInit({
    mode: anaMode,
    factoringRequired
  });

  // Legacy: compare URL referenceId to context referenceId — they share an ID space.
  // History flow: historyId and ana.referenceId (what gets stored in context as
  // `referenceId`) are different ID spaces, so we can't compare them directly. Use the
  // emptiness of context.referenceId as the "not loaded yet" signal — initRenewOrEdit
  // sets it from the response.
  const shouldLoadAna = (historyId ? !referenceId : id !== referenceId) && mode !== AnaMode.NEW;
  const {
    isLoading: anaLoadingLegacy
  } = useAnamnesisById({
    enabled: shouldLoadAna && !historyId,
    anamnesisReferenceId: id,
    onSuccess: initRenewOrEdit
  });
  const {
    isLoading: anaLoadingFromHistory
  } = useAnamnesisFromHistory({
    enabled: shouldLoadAna && Boolean(historyId),
    anamnesisHistoryReferenceId: historyId,
    onSuccess: initRenewOrEdit
  });
  const anaLoading = anaLoadingLegacy || anaLoadingFromHistory;
  const shouldLoadPerson = id !== clinicReferenceId && mode === AnaMode.NEW;
  const {
    loading: personLoading
  } = usePerson({
    skip: !shouldLoadPerson,
    onCompleted: data => {
      initNew({
        clinicReferenceId: id,
        person: data.person
      });
    }
  });
  useEffect(() => {
    dispatch(setLoading(false));
  }, []);
  const onSubmit = values => {
    dispatch(setAnswers(values));
  };
  const send = payload => {
    openDialog(DialogNames.Signature, {
      patient: `${answers.firstName} ${answers.lastName}`,
      onSubmit: signature => {
        mutateAna({
          signatureEncoded: stripBase(signature),
          formData: {
            ...answers,
            birthday: formatBirthday(answers.birthday || ''),
            holderBirthday: formatBirthday(answers.holderBirthday || ''),
            contactPersonBirthday: formatBirthday(answers.contactPersonBirthday || ''),
            insuranceCompanyId: answers.insuranceCompanyId || null,
            insuranceCompany: answers.insuranceCompanyId ? undefined : answers.insuranceCompany,
            dentalInsuranceCompanyId: answers.dentalInsuranceCompanyId || null,
            livesOnDifferentAddress: answers.livesOnDifferentAddress || false,
            dentalInsuranceCompany: answers.dentalInsuranceCompanyId ? undefined : answers.dentalInsuranceCompany
          },
          clinicReferenceId
        }, {
          onSuccess: () => {
            closeDialog();
            mutateOptins(payload, {
              onSuccess: goToNextStep
            });
          }
        });
      }
    });
  };
  return /*#__PURE__*/React.createElement(InternalTemplate, {
    Prefooter: EndBarContent || /*#__PURE__*/React.createElement(EndBar, {
      "data-attr": EndBarDataAttr
    })
  }, anaLoading || shouldLoadAna || shouldLoadPerson || personLoading ? null : /*#__PURE__*/React.createElement(S.Root, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StepperProgress, {
    finished: allDone,
    steps: Math.max(...Object.values(indices)) + 1,
    current: indices[step]
  }), /*#__PURE__*/React.createElement(S.Title, null, /*#__PURE__*/React.createElement(Translate, null, titles[step])), /*#__PURE__*/React.createElement(SubTitle, null, /*#__PURE__*/React.createElement(Translate, null, subTitles(optins)[step]))), typeof children === 'function' ? children({
    optins,
    goToNextStep,
    onSubmit,
    send,
    answers,
    EndBarDataAttr,
    factoringAgreementMissing,
    loading
  }) : children));
};
export default AnamnesisScreen;